/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { DispatchBoardService } from '../../../../projects/dispatch-board/src/app/components/dispatch-board/dispatch-board.service';
import RRule, { Frequency, Options, rrulestr, Weekday } from 'rrule';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RecurringRouteService {
  dateTimeFormat: string = this.utilityService.dateTimeFormat;
  timeFormat: string = this.utilityService.timeFormat;
  distanceUnit: string = this.utilityService.companyConfig?.general?.distance_metric === 'MILES' ? this.utilityService.getLocaleValue('route.distance.metric.miles', 'Mile(s)').toLowerCase() : this.utilityService.getLocaleValue('route.distance.metric.kilo_meters', 'Km(s)').toLowerCase();
  recurringRouteDetailsPerformAction: Subject<any> = new Subject<any>();

  constructor(
    private dataCollectorService: DataCollectorService,
    private toastService: HotToastService,
    private utilityService: UtilityService,
    private dispatchBoardService: DispatchBoardService
  ) { }

  getRecurringRouteAssignmentUrl(recurringRouteUid: string): string {
    return `${this.dataCollectorService.baseurl()}/routes/recurring/${recurringRouteUid}/assignment`;
  }

  assignUnassignRecurringRoute(recurringRouteUid: string, payload: any, showSuccessToast: boolean, statusCallback: (status: boolean) => void): void {
    this.dataCollectorService.postData(this.getRecurringRouteAssignmentUrl(recurringRouteUid), payload)
      .subscribe((response: any) => {
        if (showSuccessToast) {
          this.toastService.success(response.message);
        }
        statusCallback(true);
      }, (error: any) => {
        statusCallback(false);
      });
  }

  addJobsInRecuuringRouteMaster(recurringRouteUid: string, payload: string, showSuccessToast: boolean = true, statusCallback: (status: boolean) => void): void {
    const addJobUrl = `${this.dataCollectorService.baseurl()}/routes/recurring/${recurringRouteUid}/job`;
    this.dataCollectorService.postData(addJobUrl, payload).subscribe((response: any) => {
      if (showSuccessToast) {
        this.toastService.success(response.message);
      }
      statusCallback(true);
    },
      (error: any) => {
        statusCallback(false);
      }
    );
  }

  deleteRecurringRouteMaster(recurringRouteUid: string, showSuccessToast: boolean = true, statusCallback: (status: boolean) => void): void {
    const deleteRrMasterUrl = `${this.dataCollectorService.baseurl()}/routes/recurring/${recurringRouteUid}`;
    this.dataCollectorService.deleteData(deleteRrMasterUrl).subscribe((response: any) => {
      if (showSuccessToast) {
        this.toastService.success(response.message);
      }
      statusCallback(true);
    },
      (error: any) => {
        statusCallback(false);
      }
    );
  }

  deleteSingleJobInRecuuringRouteMaster(recurringRouteUid: string, oldRecurringJobMasterUid: string, showSuccessToast: boolean = true, statusCallback: (status: boolean) => void): void {
    const deleteJobUrl = `${this.dataCollectorService.baseurl()}/routes/recurring/${recurringRouteUid}/job/${oldRecurringJobMasterUid}`;
    this.dataCollectorService.deleteData(deleteJobUrl, {}).subscribe((response: any) => {
      if (showSuccessToast) {
        this.toastService.success(response.message);
      }
      statusCallback(true);
    },
      (error: any) => {
        statusCallback(false);
      }
    );
  }

  parseUserAssignment(oldAssignments: any[], newAssignments: any[]): { assign: { users: any[] }; unAssign: { users: any[] } } {
    const oldUsers = oldAssignments.map((item: any) => item?.user.user_uid);
    const newUsers = newAssignments.map((item: any) => item?.user.user_uid);
    const unAssign = { users: [] };
    const assign = { users: [] };
    oldAssignments.forEach((oldUser: any) => {
      const oldUserUid = oldUser.user.user_uid;
      const oldTeamUid = oldUser.team.team_uid;
      if (!newUsers.includes(oldUserUid)) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        unAssign.users.push({ user_uid: oldUserUid, team_uid: oldTeamUid });
      }
    });
    newAssignments.forEach((newUser: any) => {
      const newUserUid = newUser.user.user_uid;
      const newTeamUid = newUser.team.team_uid;
      if (!oldUsers.includes(newUserUid)) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        assign.users.push({ user_uid: newUserUid, team_uid: newTeamUid });
      }
    });
    return { unAssign: unAssign, assign: assign };
  }

  updateRouteAssignments(routeUid: string, oldAssignments: any[], newAssignments: any[], showSuccessToast: boolean, statusCallback: (completedStatus: any[]) => void): void {
    const assignmentStatus = this.parseUserAssignment(oldAssignments, newAssignments);
    const unassignPayload = assignmentStatus.unAssign;
    const assignPayload = assignmentStatus.assign;
    if (unassignPayload.users.length && assignPayload.users.length) {
      this.dispatchBoardService.unAssignRoute(routeUid, unassignPayload, false, (status: boolean) => {
        if (status) {
          this.dispatchBoardService.assignRoute(routeUid, assignPayload, false, (_status: boolean) => {
            if (_status) {
              this.toastService.success(this.utilityService.getLocaleValue('dispatchBoard.route.success.route_assignment_updated', 'Route Assignment Updated'));
              statusCallback(['unassign', 'assign']);
            }
          });
        }
      });
    }
    else if (unassignPayload.users.length) {
      this.dispatchBoardService.unAssignRoute(routeUid, unassignPayload, showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['unassign']);
        }
      });
    }
    else if (assignPayload.users.length) {
      this.dispatchBoardService.assignRoute(routeUid, assignPayload, showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['assign']);
        }
      });
    }
    else {
      statusCallback([]);
    }
  }

  updateRecurringRouteJobAssignments(recurringRouteUid: string, oldJobAssignments: any[], newJobAssignments: any[], showSuccessToast: boolean, statusCallback: (completedStatus: any[]) => void): void {
    const oldJobs = oldJobAssignments.map((job: any) => job?.recurring_job_uid);
    const newJobs = newJobAssignments.map((job: any) => job?.recurring_job_uid);
    let removeJobs = [];
    let addJobs = [];
    oldJobs.forEach((oldJob: any) => {
      if (!newJobs.includes(oldJob)) {
        removeJobs.push(oldJob);
      }
    });
    newJobs.forEach((newJob: any) => {
      if (!oldJobs.includes(newJob)) {
        addJobs.push(newJob);
      }
    });
    addJobs = _.compact(addJobs);
    removeJobs = _.compact(removeJobs);
    if (addJobs.length && removeJobs.length) {
      const addJobPayload = this.constructRecurringRouteJobAssignmentPayload('ADD', addJobs);
      this.addJobsInRecuuringRouteMaster(recurringRouteUid, addJobPayload, false, (status: boolean) => {
        if (status) {
          this.deleteSingleJobInRecuuringRouteMaster(recurringRouteUid, removeJobs[0], false, (_status: boolean) => {
            if (_status) {
              this.toastService.success(this.utilityService.getLocaleValue('recurring_route.toast.route_jobs_updated', 'Route Jobs Updated'));
              statusCallback(['addJob', 'removeJob']);
            }
            else{
              statusCallback(['addJob']);
            }
          });
        }
        else{
          statusCallback([]);
        }
      });
    }
    else if (removeJobs.length) {
      this.deleteSingleJobInRecuuringRouteMaster(recurringRouteUid, removeJobs[0], showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['removeJob']);
        }
        else{
          statusCallback([]);
        }
      });
    }
    else if (addJobs.length) {
      const addJobPayload = this.constructRecurringRouteJobAssignmentPayload('ADD', addJobs);
      this.addJobsInRecuuringRouteMaster(recurringRouteUid, addJobPayload, showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['addJob']);
        }
        else{
          statusCallback([]);
        }
      });
    }
    else {
      statusCallback([]);
    }
  }

  constructRecurringRouteJobAssignmentPayload(type: 'ADD' | 'REMOVE', jobUids: any[]): any {
    if (type === 'ADD') {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const payload = { 'recurring_jobs': [] };
      jobUids.forEach((jobUid: string) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payload['recurring_jobs'].push({ 'recurring_job_uid': jobUid });
      });
      return payload;
    }
  }

  getRecurringRouteAssignmentPayload(type: 'ASSIGN' | 'UNASSIGN', assignedTo: any[], updateAllRoutes: boolean = true): any {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'assignment_data': { 'assigned_to': assignedTo, 'assign_type': type, 'update_all_routes': updateAllRoutes, 'notify_users': true }
    };
  }

  updateRecurringRouteUserAssignments(recurringRouteUid: string, oldAssignments: any[], newAssignments: any[], showSuccessToast: boolean, statusCallback: (completedStatus: any[]) => void): void {
    const assignmentStatus = this.parseUserAssignment(oldAssignments, newAssignments);
    const unassignPayload = this.getRecurringRouteAssignmentPayload('UNASSIGN', assignmentStatus.unAssign.users);
    const assignPayload = this.getRecurringRouteAssignmentPayload('ASSIGN', assignmentStatus.assign.users);
    if (assignmentStatus.unAssign.users.length && assignmentStatus.assign.users.length) {
      this.assignUnassignRecurringRoute(recurringRouteUid, unassignPayload, false, (status: boolean) => {
        if (status) {
          this.assignUnassignRecurringRoute(recurringRouteUid, assignPayload, false, (_status: boolean) => {
            if (_status) {
              this.toastService.success(this.utilityService.getLocaleValue('dispatchBoard.route.success.route_assignment_updated', 'Route Assignment Updated'));
              statusCallback(['unassign', 'assign']);
            }
          });
        }
      });
    }
    else if (assignmentStatus.unAssign.users.length) {
      this.assignUnassignRecurringRoute(recurringRouteUid, unassignPayload, showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['unassign']);
        }
      });
    }
    else if (assignmentStatus.assign.users.length) {
      this.assignUnassignRecurringRoute(recurringRouteUid, assignPayload, showSuccessToast, (status: boolean) => {
        if (status) {
          statusCallback(['assign']);
        }
      });
    }
    else {
      statusCallback([]);
    }
  }

  getRecurringRouteListConflicts(recurringRouteUid: string, timezone: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const routeConflictsUrl = `${this.dataCollectorService.baseurl()}/routes/recurring/conflicts`;
      const payload = {
        // eslint-disable-next-line max-len, @typescript-eslint/naming-convention
        'conflict_count_only': true, 'timezone': timezone, 'move_all_recurring_job_instances': false, 'route_conflict': { 'conflict_check_types': ['USER', 'DATE', 'DURATION', 'SKILL', 'SLOT'], 'operation_type': 'route_creation_job_assignment', 'data': { 'recurring_route_uid': recurringRouteUid } }
      };
      this.dataCollectorService.postData(routeConflictsUrl, payload).subscribe(
        (response: any) => {
          resolve(response?.data || {});
        },
        (error: any) => {
          resolve({});
        });
    });
  }

  getRecurringRouteInstanceConflicts(routeInstanceDetails: any, timezone: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const targetRouteUid = routeInstanceDetails?.route_uid;
      let recurringJobUids: any[] = routeInstanceDetails?.jobs.map((job: any) => (job?.job_uid || job?.job?.job_uid));
      recurringJobUids = _.compact(recurringJobUids);
      if (targetRouteUid && recurringJobUids.length) {
        const routeConflictsUrl = `${this.dataCollectorService.baseurl()}/routes/recurring/conflicts`;
        const payload = {
          // eslint-disable-next-line max-len, @typescript-eslint/naming-convention
          'conflict_count_only': false, 'timezone': timezone, 'move_all_recurring_job_instances': false, 'route_conflict': { 'conflict_check_types': ['USER', 'DATE', 'DURATION', 'SKILL', 'SLOT'], 'operation_type': 'route_creation_job_assignment', 'data': { 'target_route_uid': targetRouteUid, 'recurring_job_uids': recurringJobUids } }
        };
        this.dataCollectorService.postData(routeConflictsUrl, payload).subscribe(
          (response: any) => {
            const conflicts = response?.data || {};
            let totalConflicts = 0;
            if (conflicts?.length) {
              conflicts.forEach((conflict: any) => { totalConflicts += conflict?.count; });
            }
            resolve({ conflicts: conflicts, totalConflicts: totalConflicts || 0 });
          },
          (error: any) => {
            resolve({ conflicts: [], totalConflicts: 0 });
          });
      }
      else {
        resolve({ conflicts: [], totalConflicts: 0 });
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  getDistanceBetweenJobs(routeDetails: { SOURCE?: any; DESTINATION?: any }, existingCustomObj: { SOURCE?: any; DESTINATION?: any } = { SOURCE: null, DESTINATION: null }, existingDistanceData: { SOURCE?: any; DESTINATION?: any } = { SOURCE: null, DESTINATION: null }, statusCallBack: (distanceData: any) => void): void {
    const customObj = this.constructDistanceBetweenJobsObj(routeDetails, existingCustomObj);
    const payload = this.constructDistanceBetweenJobsPayload(customObj?.optimizedDistanceObj, routeDetails);
    const distanceBetweenJobsUrl = `${this.dataCollectorService.baseurl()}/location/dispatch_board/routing`;
    if (payload?.routes?.length) {
      this.dataCollectorService.postData(distanceBetweenJobsUrl, payload)
        .subscribe(
          (response: any) => {
            const distanceData = this.convertDistanceDataToCustomObj(response?.data, routeDetails, existingDistanceData);
            statusCallBack({ distanceData: distanceData, customObj: customObj?.masterCustomObj });
          },
          (error: any) => {
            statusCallBack({ distanceData: {}, customObj: {} });
          });
    }
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  constructDistanceBetweenJobsObj(routeDetails: { SOURCE?: any; DESTINATION?: any }, existingCustomObj: { SOURCE?: any; DESTINATION?: any } = { SOURCE: null, DESTINATION: null }): any {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const masterCustomObj = { SOURCE: {}, DESTINATION: {} }; const optimizedDistanceObj = { SOURCE: {}, DESTINATION: {} };
    Object.keys(routeDetails).forEach((type: any) => {
      let customObj = {};
      masterCustomObj[type]['legDetails'] = {};
      //Start location
      const [hasRouteStartLocation, routeStartLocation] = this.getRouteGeoCords(routeDetails[type], 'start');
      if (hasRouteStartLocation) {
        customObj['fromUid'] = `${type}-START`;
        customObj['fromGeoCords'] = routeStartLocation;
      }

      //Job locations
      if (routeDetails?.[type]?.jobs?.length) {
        _.forEach(routeDetails[type].jobs, (routeJob: any, index: number) => {
          const [hasJobGeoCords, jobLocation] = this.getRouteJobGeoCords(routeJob);
          if (hasJobGeoCords) {
            if (!customObj['fromGeoCords']) {
              customObj['fromUid'] = routeJob?.job_uid || routeJob?.job?.job_uid;
              customObj['fromGeoCords'] = jobLocation;
            }
            else {
              customObj['toUid'] = routeJob?.job_uid || routeJob?.job?.job_uid;
              customObj['toGeoCords'] = jobLocation;
            }
            if (customObj?.['fromGeoCords'] && customObj?.['toGeoCords'] && Object.keys(customObj || {}).length) {
              const key = `${customObj?.['fromUid']};${customObj?.['toUid']}`;
              masterCustomObj[type]['legDetails'][key] = customObj;
              customObj = { 'fromUid': customObj?.['toUid'], 'fromGeoCords': customObj?.['toGeoCords'] };
            }
          }
          else {
            if (customObj?.['toUid'] && customObj?.['toGeoCords']) {
              customObj = { 'fromUid': customObj?.['toUid'], 'fromGeoCords': customObj?.['toGeoCords'] };
            }
            else {
              customObj = {};
            }
          }
        });
      }

      //End Location
      const [hasRoutEndLocation, routEndLocation] = this.getRouteGeoCords(routeDetails[type], 'end');
      if (hasRoutEndLocation) {
        customObj['toUid'] = `${type}-END`;
        customObj['toGeoCords'] = routEndLocation;
      }
      if (customObj?.['fromGeoCords'] && customObj?.['toGeoCords'] && Object.keys(customObj || {}).length) {
        const key = `${customObj?.['fromUid']};${customObj?.['toUid']}`;
        masterCustomObj[type]['legDetails'][key] = customObj;
      }
      masterCustomObj[type]['routeUid'] = routeDetails[type]?.route_uid;
      masterCustomObj[type]['type'] = type;

      //Optimize
      const masterCustomKeys = Object.keys(masterCustomObj[type]?.['legDetails'] || {});
      const existingCustomKeys = Object.keys(existingCustomObj[type]?.['legDetails'] || {});
      const differenceCustomKeys = _.difference(masterCustomKeys, existingCustomKeys);
      optimizedDistanceObj[type]['routeUid'] = masterCustomObj[type]['routeUid'];
      optimizedDistanceObj[type]['type'] = masterCustomObj[type]['type'];
      optimizedDistanceObj[type]['legDetails'] = {};
      differenceCustomKeys.forEach((customKey: string) => {
        optimizedDistanceObj[type]['legDetails'][customKey] = masterCustomObj[type]['legDetails'][customKey];
      });
    });

    return { masterCustomObj: masterCustomObj, optimizedDistanceObj: optimizedDistanceObj };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  constructDistanceBetweenJobsPayload(customObj: { SOURCE?: any; DESTINATION?: any }, routeDetails: { SOURCE?: any; DESTINATION?: any }): any {
    const payload = { routes: [] };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const mapTransportType = { 'CAR': 'car', 'TRUCK': 'car', 'PEDESTRIAN': 'foot' };
    Object.keys(customObj || {}).forEach((type: any) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const customPayload = { user_uid: customObj[type]?.routeUid, vehicle: mapTransportType[routeDetails[type]?.transport_mode] || 'car', jobs: [] };
      const legDetails = Object.keys(customObj[type]['legDetails'] || {});
      legDetails.forEach((legDetail: any, index: number) => {
        const distanceData = customObj[type]['legDetails'][legDetail];
        // eslint-disable-next-line @typescript-eslint/naming-convention
        customPayload.jobs.push({ job_uid: distanceData?.fromUid, lat: distanceData?.fromGeoCords[0], lng: distanceData?.fromGeoCords[1] });
        if (index === legDetails?.length - 1) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          customPayload.jobs.push({ job_uid: distanceData?.toUid, lat: distanceData?.toGeoCords[0], lng: distanceData?.toGeoCords[1] });
        }
      });
      if (customPayload.jobs.length >= 2) {
        payload.routes.push(customPayload);
      }
    });
    return payload;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  convertDistanceDataToCustomObj(distanceResponse: any[], routeDetails: { SOURCE?: any; DESTINATION?: any }, existingDistanceData: { SOURCE?: any; DESTINATION?: any } = { SOURCE: null, DESTINATION: null }): any {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const distanceData = { SOURCE: null, DESTINATION: null };
    distanceResponse.forEach((distanceObj: any) => {
      const routeUid = distanceObj?.user_uid;
      if (routeUid) {
        let type: 'SOURCE' | 'DESTINATION';
        if (routeDetails.SOURCE?.route_uid === routeUid) {
          type = 'SOURCE';
        }
        else if (routeDetails.DESTINATION?.route_uid === routeUid) {
          type = 'DESTINATION';
        }
        distanceData[type] = {};
        if (distanceObj?.leg_details?.length) {
          distanceObj?.leg_details.forEach((legDetail: any) => {
            const sourceUid = legDetail?.start_location?.job_uid;
            const travelTime = Math.round(legDetail?.leg_time / 1000);
            const time: string = this.utilityService.convertSecondsToHours(travelTime, true);
            const distance: string = (this.utilityService.getDistance(legDetail?.leg_distance) || 0) + ' ' + this.distanceUnit;
            distanceData[type][sourceUid] = { travelTime: time, travelDistance: distance };
          });
        }
      }
    });
    distanceData.SOURCE = _.merge(existingDistanceData.SOURCE, distanceData.SOURCE);
    distanceData.DESTINATION = _.merge(existingDistanceData.DESTINATION, distanceData.DESTINATION);
    return distanceData;
  }

  moveRouteJobs(sourceRouteDetails: any, destinationRouteDetails: any, updateAllInstances: boolean, statusCallBack: (status: boolean) => void): any {
    const sourceRouteUid = sourceRouteDetails?.route_uid;
    const sourceJobs = sourceRouteDetails?.jobs || [];
    const destinationRouteUid = destinationRouteDetails?.route_uid;
    const destinationJobs = destinationRouteDetails?.jobs || [];
    if (!sourceRouteUid || !destinationRouteUid) {
      statusCallBack(false);
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const payload = { movement_data: { source: { route_uid: null, jobs: [] }, destination: { route_uid: null, jobs: [] }, update_all_instances_for_removed_job: updateAllInstances } };
    payload.movement_data.source.route_uid = sourceRouteUid;
    payload.movement_data.destination.route_uid = destinationRouteUid;
    sourceJobs.forEach((sourceJob: any, index: number) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      payload.movement_data.source.jobs.push({ job_uid: sourceJob?.job_uid || sourceJob?.job?.job_uid, sequence: index + 1, update_all_instances: updateAllInstances });
    });
    destinationJobs.forEach((destinationJob: any, index: number) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      payload.movement_data.destination.jobs.push({ job_uid: destinationJob?.job_uid || destinationJob?.job?.job_uid, sequence: index + 1, update_all_instances: updateAllInstances });
    });
    const jobMovementUrl = `${this.dataCollectorService.baseurl()}/routes/recurring`;
    this.dataCollectorService.putData(jobMovementUrl, payload).subscribe((response: any) => {
      statusCallBack(true);
    },
      (error: any) => {
        statusCallBack(false);
      });
  }

  jobMovementSummary(oldSourceJobs: any[], newSourceJobs: any[], oldDestinationJobs: any[], newDetinationJobs: any[]): any {
    const differenceSourceJobs = _.differenceBy(oldSourceJobs, newSourceJobs, 'job.job_uid');
    const differenceDestinationJobs = _.differenceBy(oldDestinationJobs, newDetinationJobs, 'job.job_uid');
    const movedSourceJobs = _.intersectionBy(oldSourceJobs, newDetinationJobs, 'job.job_uid');
    const movedDestinationJobs = _.intersectionBy(oldDestinationJobs, newSourceJobs, 'job.job_uid');
    const allMovedJobs = _.unionBy(movedSourceJobs, movedDestinationJobs, 'job.job_uid');
    const deletedSourceJobs = _.differenceBy(differenceSourceJobs, allMovedJobs, 'job.job_uid');
    const deletedDestinationJobs = _.differenceBy(differenceDestinationJobs, allMovedJobs, 'job.job_uid');
    const onlyRecurranceJobsInMovement = movedSourceJobs.every((routeJob: any) => routeJob?.job?.is_recurrence) && movedDestinationJobs.every((routeJob: any) => routeJob?.job?.is_recurrence)
      && deletedSourceJobs.every((routeJob: any) => routeJob?.job?.is_recurrence) && deletedDestinationJobs.every((routeJob: any) => routeJob?.job?.is_recurrence);
    return { deletedSourceJobs: deletedSourceJobs, deletedDestinationJobs: deletedDestinationJobs, movedSourceJobs: movedSourceJobs, movedDestinationJobs: movedDestinationJobs, onlyRecurranceJobsInMovement: onlyRecurranceJobsInMovement };
  }

  checkOccurrenceIsOne(rruleOptions: Options): boolean {
    if (rruleOptions.freq === RRule.DAILY) {
      return true;
    }
    else if (rruleOptions.freq === RRule.WEEKLY) {
      return this.checkRepeatInFreqIsOne(rruleOptions.byweekday);
    }
    else if (rruleOptions.freq === RRule.MONTHLY) {
      return this.checkRepeatInFreqIsOne(rruleOptions.bymonthday) && this.checkRepeatInFreqIsOne(rruleOptions.byweekday);
    }
    else if (rruleOptions.freq === RRule.YEARLY) {
      return this.checkRepeatInFreqIsOne(rruleOptions.bymonthday) && this.checkRepeatInFreqIsOne(rruleOptions.bymonth);
    }
  }

  checkRepeatInFreqIsOne(byValue: any): boolean {
    if (_.isNull(byValue) || _.isUndefined(byValue)) {
      return true; // No restriction means it's not causing multiple occurrences
    }
    if (typeof byValue === 'number' || typeof byValue === 'string') {
      return true;
    }
    else if (typeof byValue === 'object' && byValue?.length === 1) {
      return true;
    }
    return false;
  }

  getCustomOccurrencesCount(route: { rule: RRule; occurrences: any[] }, job: { rule: RRule; occurrences: any[] }): boolean {
    if (route.occurrences?.length && job.occurrences?.length) {
      const customRouteCount: number = moment(route.occurrences[route.occurrences?.length-1]).diff( moment(route.occurrences[0]), 'd');
      const customJobCount: number = moment(job.occurrences[job.occurrences?.length-1]).diff( moment(job.occurrences[0]), 'd');
      return customRouteCount >= customJobCount;
    }
    else {
      return false;
    }
  }

  getUniqueDays(occurrences: Date[]): number[] {
    return Array.from(new Set(occurrences.map(occ => occ.getDay()))).sort();
  }

  getUniqueDates(occurrences: Date[]): number[] {
    return Array.from(new Set(occurrences.map(occ => occ.getDate()))).sort();
  }

  getUniqueMonths(occurrences: Date[]): number[] {
    return Array.from(new Set(occurrences.map(occ => occ.getMonth() + 1))).sort();
  }

  isRecurrenceCompatible(routeRuleObj: any, jobRuleObj: any): boolean {
    const routeFreq = routeRuleObj.options.freq;
    const jobFreq = jobRuleObj.options.freq;
    const validCombinations: Record<number, number[]> = {
      [RRule.DAILY]: [RRule.DAILY, RRule.WEEKLY, RRule.MONTHLY, RRule.YEARLY],
      [RRule.WEEKLY]: [RRule.WEEKLY, RRule.MONTHLY, RRule.YEARLY],
      [RRule.MONTHLY]: [RRule.MONTHLY, RRule.YEARLY],
      [RRule.YEARLY]: [RRule.YEARLY]
    };
    return validCombinations[routeFreq]?.includes(jobFreq) ?? false;
  }

  checkScheduleAndCount(route: { rrule: string; start: Date; end: Date; routeDetails?: any }, job: { rrule: string; start: Date; end: Date; jobDetails?: any }): boolean {
    const routeRule = rrulestr(route.rrule);
    const jobRule = rrulestr(job.rrule);
    if (!this.isRecurrenceCompatible(routeRule, jobRule)) {
      return false;
    }

    const routeOccurrences = routeRule.between(route.start, route.end, true);
    const jobOccurrences = jobRule.between(job.start, job.end, true);

    if (route.routeDetails?.recurring_route?.frequency_type === 'CUSTOM' ) {
      if (moment(route.start).isAfter(moment(job.start), 'day') || moment(route.end).isBefore(moment(job.end), 'day')) {
        return false;
      }
      return jobOccurrences.every((jobOccurrence: Date) => routeOccurrences.some((routeOccurrence: Date) => moment(jobOccurrence).isSame(routeOccurrence, 'date')));
    }

    if ([RRule.DAILY, RRule.WEEKLY, RRule.MONTHLY, RRule.YEARLY].includes(routeRule.options.freq)) {
      const isRouteOneOccurrence: boolean = this.checkOccurrenceIsOne(routeRule.options);
      const isJobOneOccurrence: boolean = this.checkOccurrenceIsOne(jobRule.options);
      if (isRouteOneOccurrence && isJobOneOccurrence) {
        const isJobAndRouteCountMatch: boolean = this.getCustomOccurrencesCount({rule: routeRule, occurrences: routeOccurrences}, {rule: jobRule, occurrences: jobOccurrences});
        if (!isJobAndRouteCountMatch) {
          return false;
        }
        if (routeRule.options.freq === RRule.DAILY) {
          return true;
        }
        return true;
      }

      let routeUnique: number[] = [];
      let jobUnique: number[] = [];

      if (routeRule.options.freq === RRule.WEEKLY) {
        routeUnique = this.getUniqueDays(routeOccurrences);
        jobUnique = this.getUniqueDays(jobOccurrences);
      } else if (routeRule.options.freq === RRule.MONTHLY) {
        routeUnique = this.getUniqueDates(routeOccurrences);
        jobUnique = this.getUniqueDates(jobOccurrences);
      } else if (routeRule.options.freq === RRule.YEARLY) {
        routeUnique = this.getUniqueMonths(routeOccurrences);
        jobUnique = this.getUniqueMonths(jobOccurrences);
      }

      if (routeUnique.length === jobUnique.length && routeUnique.every((value, index) => value === jobUnique[index])) {
        return true;
      }
    }

    return false;
  }

  getRouteGeoCords(routeDetails: any, type: 'start' | 'end'): any[] {
    let hasGeoCords = false;
    let geoCords = [];
    const routeGeoCords = this.isRouteHasGeoCords(routeDetails, type);
    if (routeGeoCords) {
      if (routeGeoCords.length === 2 && typeof routeGeoCords[0] === 'number' && typeof routeGeoCords[1] === 'number' && routeGeoCords[0] && routeGeoCords[1]) {
        geoCords = routeGeoCords;
        hasGeoCords = true;
      }
    }
    return [hasGeoCords, geoCords];
  }

  getRouteJobGeoCords(routeJob: any): any[] {
    let hasGeoCords = false;
    let geoCords = [];
    const jobGeoCords = this.isJobHasGeoCords(routeJob?.job);
    if (jobGeoCords) {
      if (jobGeoCords?.length === 2 && typeof jobGeoCords[0] === 'number' && typeof jobGeoCords[1] === 'number' && jobGeoCords[0] && jobGeoCords[1]) {
        geoCords = jobGeoCords;
        hasGeoCords = true;
      }
    }
    return [hasGeoCords, geoCords];
  }

  getRruleText(rrRule: string): string {
    let rruleText: string = '';
    if (rrRule) {
      try {
        rruleText = RRule?.fromString(rrRule).toText();
      }
      catch {
        rruleText = '';
      }
    }
    return rruleText;
  }

  isJobHasGeoCords(jobDetails: any): any {
    return jobDetails?.customer_address?.geo_cordinates?.length === 2 && jobDetails?.customer_address?.geo_cordinates;
  }

  isRouteHasGeoCords(routeDetails: any, type: 'start' | 'end'): any {
    return type === 'start' ? routeDetails?.start_location?.geo_cords?.length === 2 && routeDetails?.start_location?.geo_cords : routeDetails?.end_location?.geo_cords?.length === 2 && routeDetails?.end_location?.geo_cords;
  }

  parseRrule(rRuleString: string): any {
    let rRuleSection = rRuleString;
    if (rRuleString.includes('RRULE:')) {
      const rRuleIndex = rRuleString.indexOf('RRULE:');
      rRuleSection = rRuleString.substring(rRuleIndex + 6);
    }
    const parts = rRuleSection.split(';');
    const rRuleOptions: any = {};
    parts.forEach((part) => {
      const [key, value] = part.split('=');
      switch (key) {
        case 'FREQ':
          rRuleOptions.freq = RRule[value];
          break;
        case 'INTERVAL':
          rRuleOptions.interval = parseInt(value, 10);
          break;
        case 'BYDAY':
          rRuleOptions.byweekday = this.getRRuleDay(value);
          break;
        case 'UNTIL':
          rRuleOptions.until = moment(value).toDate();
          break;
        case 'COUNT':
          rRuleOptions.count = parseInt(value, 10);
          break;
      }
    });
    return rRuleOptions;
  }

  getRRuleDay(byDay: string): Weekday[] {
    const days = byDay.split(',').map((day) => {
      const dayNumber = parseInt(day.substring(0, day.length - 2), 10);
      const dayAbbreviation = day.substring(day.length - 2);
      switch (dayAbbreviation) {
        case 'SU': return RRule.SU.nth(dayNumber);
        case 'MO': return RRule.MO.nth(dayNumber);
        case 'TU': return RRule.TU.nth(dayNumber);
        case 'WE': return RRule.WE.nth(dayNumber);
        case 'TH': return RRule.TH.nth(dayNumber);
        case 'FR': return RRule.FR.nth(dayNumber);
        case 'SA': return RRule.SA.nth(dayNumber);
        default: return null;
      }
    });
    return days;
  }

  getRouteUtilisedPercentage(routeDuration: number, totalTime: number): any {
    let utilisedPercentage: number = 0;
    const routeDurationTime = moment().startOf('d').add(routeDuration, 'h');
    const totalRouteTime = moment().startOf('d').add(totalTime, 'seconds');
    const diffHours = routeDurationTime.diff(totalRouteTime, 'h');
    utilisedPercentage = diffHours > 0 ? 100 - Math.round((diffHours / routeDuration) * 100) : 100;
    if (utilisedPercentage > 100) {
      utilisedPercentage = 100;
    }
    else if (utilisedPercentage < 0) {
      utilisedPercentage = 0;
    }
    return utilisedPercentage;
  }

  getRouteHoursRemaining(routeDuration: number, totalTime: number): any {
    let formattedDiff: string = '0h';
    const routeDurationTime = moment().startOf('d').add(routeDuration, 'h');
    const totalRouteTime = moment().startOf('d').add(totalTime, 'seconds');
    const diffMilliSec = routeDurationTime.diff(totalRouteTime, 'seconds');
    if (diffMilliSec) {
      formattedDiff = this.utilityService.convertSecondsToHours(diffMilliSec, true);
    }
    return formattedDiff;
  }

  //Route date picker Dateclass
  routeDatePickerDateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if ((moment(cellDate).date() === 1) && (view === 'month')) {
      this.getRouteCountForDateClass(cellDate);
    };
    return '';
  };

  getRouteCountForDateClass(date: any): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const filter = { 'from_date': date.startOf('month').format('YYYY-MM-DD'), 'to_date': date.endOf('month').format('YYYY-MM-DD') };
    const routeCountUrl = `${this.dataCollectorService.baseurl()}/routes/count`;
    this.dataCollectorService.getData(routeCountUrl, filter)
      .subscribe((res: any) => {
        const allRouteCOunts = res?.data;
        const calendarDates = document.querySelectorAll('.mat-calendar-body-cell-content');
        allRouteCOunts.forEach((route: any, i: number) => {
          if (route?.job_routes > 0) {
            calendarDates[i].classList.add('route-available');
          } else {
            calendarDates[i].classList.remove('route-available');
          }
        });
      });
  }

  getStartEndTime(startDateTime: any, endDateTime: any, onlyTime: boolean = false, timezone: string = this.utilityService.timeZone, dayOfTheWeek: boolean = false): string[] {
    dayjs.tz.setDefault(timezone);
    if (!startDateTime || !endDateTime) {
      return [];
    }
    if (!timezone) {
      timezone = this.utilityService.timeZone;
    }
    const formatedStartEndTime: string[] = [];
    startDateTime = dayjs(startDateTime).tz(timezone);
    endDateTime = dayjs(endDateTime).tz(timezone);
    const isSameDate = startDateTime.isSame(endDateTime, 'd');
    if (dayOfTheWeek) {
      formatedStartEndTime.push(startDateTime.format(onlyTime ? this.timeFormat : `${this.utilityService.dateFormat} (ddd) ${this.timeFormat}`));
    }
    else {
      formatedStartEndTime.push(startDateTime.format(onlyTime ? this.timeFormat : this.dateTimeFormat));
    }
    formatedStartEndTime.push(endDateTime.format(isSameDate || onlyTime ? this.timeFormat : this.dateTimeFormat));
    return formatedStartEndTime;
  }

  formatDateTime(dateTime: any, format: string = this.dateTimeFormat, timezone: string = this.utilityService.timeZone): string {
    if (!timezone) {
      timezone = this.utilityService.timeZone;
    }
    return dayjs(dateTime).tz(timezone).format(format);
  }

  getAddressText(address: any): string {
    return _.compact([address?.street, address?.landmark, address?.city || address?.name, address?.state, address?.zipcode || address?.zip_code, address?.country?.label || (_.isString(address?.country) ? address?.country : '')]).join(', ');
  }

  getTitle(prefix: string, eventNumber: string, eventTitle: string): string {
    return `#${prefix || ''}${eventNumber || ''} ${eventTitle || ''}`;
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  get moveSchedulerConfig(): any {
    return {
      'scheduler': {
        'primary_color': 'jobCategory', 'secondary_color': 'team', 'card_view': { '1': [{ 'label': 'Job #', 'originalLabel': 'Job #', 'group_uid': 'work_order_number', 'group_name': 'Default Fields', 'type': 'SINGLE_LINE', 'value': '#98458', 'selector': 'work_order_number', 'dismissable': false, 'isDefaultField': true, 'key': 'work_order_number@Job-#', 'row': 1, 'column': 1 }, { 'label': 'Job Title', 'originalLabel': 'Job Title', 'group_uid': 'work_order_title', 'group_name': 'Default Fields', 'type': 'SINGLE_LINE', 'value': 'Skiles and Sons', 'selector': 'job_title', 'dismissable': false, 'isDefaultField': true, 'key': 'work_order_title@Job-Title', 'row': 1, 'column': 2 }], '2': [{ 'label': 'Current Job Status', 'group_uid': 'current_work_order_status', 'group_name': 'Default Fields', 'type': 'SINGLE_LINE', 'value': 'Started', 'status_color_selector': 'current_job_status.status_color', 'selector': 'current_job_status.status_name', 'dismissable': true, 'isDefaultField': true, 'key': 'current_work_order_status@Current-Job-Status', 'row': 2, 'column': 2 }, { 'label': 'Customer Name', 'group_uid': 'work_order_customer_name', 'group_name': 'Default Fields', 'type': 'SINGLE_LINE', 'value': 'Jose-Manuel', 'selector': ['customer.customer_first_name', 'customer.customer_last_name'], 'icon': 'ti ti-user-circle', 'dismissable': true, 'isDefaultField': true, 'key': 'work_order_customer_name@Customer-Name', 'row': 2, 'column': 2 }], '3': [{ 'label': 'Scheduled Date & Time', 'group_uid': 'work_order_scheduled_date', 'group_name': 'Default Fields', 'type': 'DATE', 'value': '02/02/2024 09:00 AM -> 02/02/2024 10:00 AM', 'selector': ['scheduled_start_time', 'scheduled_end_time'], 'icon': 'ti ti-calendar-time', 'dismissable': true, 'isDefaultField': true, 'key': 'work_order_scheduled_date@Scheduled-Date-&-Time', 'row': 3, 'column': 1 }] },
        'mode_of_transport': 'CAR', 'display': { 'multiple_teams': false, 'business_hours_only': false, 'duration_between_jobs': true, 'job_summary': true, 'weekends': true, 'week_numbers': false, 'multiple_timezone': true }
      }
    };
  }
  /* eslint-enable @typescript-eslint/naming-convention */
}
